import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Badge, Box, Button, Grid, Rating, Typography, Theme, TextField, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { parsePhoneNumber, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
// import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { formatTimestamp, formatTimestamp2 } from '../../utils/formatters';
import { useAppDispatch, useTypedSelector } from '../../store';
import {
  deleteGbpReviewReply,
  replyToGbpReview,
  generateGbpReviewReply,
  clearGeneratedReply,
  sendVideoInvitation,
  fetchGbpReviewById,
} from '../../store/slices/gbpSlice';
import { useTrackEvent } from '../../hooks/useTrackEvent';

const useStyles = makeStyles((theme: Theme) => ({
  reviewContainer: {
    padding: theme.spacing(1, 0),
  },
  avatar: {
    backgroundColor: '#A0C3FE',
    color: '#4374E0'
  },
  profileName: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  timestamp: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  reviewText: {
    marginTop: theme.spacing(0.5),
  },
  replyButton: {
    borderRadius: '20px',
    textTransform: 'none',
    float: 'right',
    
  },
  moreLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 'bold',
    marginLeft: theme.spacing(0.5),
  },
  replyContainer: {
    marginTop: theme.spacing(2),
  },
  businessAvatar: {
    backgroundColor: '#A0C3FE',
    color: '#4374E0'
  },
  ownerLabel: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  editDeleteButton: {
    textTransform: 'none',
    // color: theme.palette.text.secondary,
  },
  replyText: {
    marginTop: theme.spacing(0.5),
  },
  actionButtons: {
    marginTop: theme.spacing(1),
    '& > button': {
      marginRight: theme.spacing(1),
    },
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100px', // Adjust as needed
  },
  generatedReplyContainer: {
    marginTop: '8px',
    padding: '8px',
    marginBottom: '8px',
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  generatedReplyText: {
    fontStyle: 'italic',
  },
  aiAssistHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  aiAssistTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  aiIcon: {
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    display: 'none',
  },
  videoInviteBlock: {
    position: 'fixed',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 50px)',
    maxWidth: '600px',
    padding: theme.spacing(3),
    backgroundColor: '#F5F7FA',
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    // Responsive scaling for smaller screens
    [theme.breakpoints.down('sm')]: {
      bottom: '20px',
      padding: theme.spacing(2),
      width: 'calc(100% - 32px)',
    },
    // Extra small screens (mobile)
    '@media (max-height: 700px)': {
      bottom: '16px',
      padding: theme.spacing(1.5, 2),
    },
  },
  videoInviteTitle: {
    fontWeight: 600,
    fontSize: '1.125rem',
    marginBottom: theme.spacing(1),
    color: '#1e293b',
    // Smaller font on mobile
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(0.75),
    },
    '@media (max-height: 700px)': {
      fontSize: '0.9375rem',
      marginBottom: theme.spacing(0.5),
    },
  },
  videoInviteDescription: {
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: theme.spacing(2),
    // Smaller font and spacing on mobile
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8125rem',
      marginBottom: theme.spacing(1.5),
    },
    '@media (max-height: 700px)': {
      fontSize: '0.75rem',
      marginBottom: theme.spacing(1),
    },
  },
  askVideoButton: {
    backgroundColor: '#5242EA',
    color: 'white',
    textTransform: 'none',
    padding: theme.spacing(1.5, 4),
    '&:hover': {
      backgroundColor: '#4133D1',
    },
    // Smaller button on mobile
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.25, 3),
      fontSize: '0.875rem',
    },
    '@media (max-height: 700px)': {
      padding: theme.spacing(1, 2.5),
      fontSize: '0.8125rem',
    },
  },
  modalTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: '#1e293b',
  },
  modalDescription: {
    color: '#64748b',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  sendSmsButton: {
    backgroundColor: '#5242EA',
    color: 'white',
    textTransform: 'none',
    padding: theme.spacing(1.5),
    width: '100%',
    '&:hover': {
      backgroundColor: '#B0B3CC',
    },
    '&:disabled': {
      backgroundColor: '#E0E0E0',
      color: '#9E9E9E',
    },
    marginTop: theme.spacing(1),
  },
  successScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 3),
    textAlign: 'center',
    height: '100%',
    minHeight: '400px',
  },
  successIcon: {
    fontSize: '80px',
    color: '#5242EA',
    marginBottom: theme.spacing(3),
  },
  successTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#101828',
    marginBottom: theme.spacing(2),
  },
  successMessage: {
    fontSize: '16px',
    color: '#667085',
    lineHeight: '24px',
    marginBottom: theme.spacing(4),
  },
  backToReviewButton: {
    color: '#5242EA',
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 600,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'transparent',
    },
  },
}));

interface ReplyData {
  businessName: string;
  text: string;
  timestamp: string;
}

interface ReviewProps {
  profileName: string;
  timestamp: string;
  rating: number;
  reviewText?: string;
  profilePhotoUrl?: string;
  reply?: ReplyData;
  reviewId: string;
  accountId: string;
  submittingReviewId: string | null;
  invitationId?: string | null;
}

export const Review: React.FC<ReviewProps> = ({
  profileName,
  timestamp,
  rating,
  reviewText,
  profilePhotoUrl,
  reply,
  reviewId,
  accountId,
  submittingReviewId,
  invitationId,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { generatingReply, generatedReplies } = useTypedSelector((state) => state.gbpSlice.reviews);
  const originalToken = useTypedSelector((state) => state.gbpSlice.originalToken);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [replyText, setReplyText] = useState(reply?.text || '');
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isDeletingReply, setIsDeletingReply] = useState(false);
  const { trackEvent } = useTrackEvent();
  const characterLimit = 150;
  const isTruncated = reviewText && reviewText.length > characterLimit;
  
  // Store the original reply text to restore on cancel
  const originalReplyText = useRef(reply?.text || '');
  // Track previous generated reply to detect actual changes
  const prevGeneratedReply = useRef<string | undefined>();

  const isGenerating = generatingReply.reviewId === reviewId;
  const generatedReply = generatedReplies[reviewId];

  const isSubmitting = submittingReviewId === reviewId;

  const toggleIsExpanded = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    
    trackEvent(newExpandedState ? 'review_text_expanded' : 'review_text_collapsed', {
      page: 'review_detail'
    });
  };

  const handleReply = () => {
    if (originalToken) {
      trackEvent('reply_submitted', {
        value: replyText.length, // Override with reply length instead of star rating
        page: 'review_detail'
      });
      
      dispatch(replyToGbpReview({ accountId, reviewId, comment: replyText, token: originalToken }));
      setIsReplying(false);
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    if (originalToken) {
      trackEvent('reply_deleted', {
        page: 'review_detail'
      });
      
      setIsDeletingReply(true);
      try {
        await dispatch(deleteGbpReviewReply({ accountId, reviewId, token: originalToken })).unwrap();
      } catch (error) {
        console.error('Failed to delete reply:', error);
      } finally {
        setIsDeletingReply(false);
      }
    }
  };

  const handleGenerateReply = () => {
    if (originalToken) {
      trackEvent('reply_generate_requested', {
        page: 'review_detail'
      });
      
      dispatch(generateGbpReviewReply({ accountId, reviewId, token: originalToken })).then((res: any) => {
        if (res.payload?.generatedReply) {
          setReplyText(res.payload.generatedReply);
          trackEvent('reply_generated_success', {
            value: res.payload.generatedReply.length,
            page: 'review_detail'
          });
        }
      });
    }
  };

  const handleSendInvitation = async () => {
    if (originalToken && isPhoneValid) {
      setIsSendingInvitation(true);
      try {
        await dispatch(sendVideoInvitation({
          accountId,
          reviewId,
          phoneNumber: `+1${phoneNumber}`,
          token: originalToken
        })).unwrap();
        
        // On success, show success screen
        setShowSuccessScreen(true);
        setShowPhoneModal(false);
        
        trackEvent('video_invitation_sent', {
          page: 'review_detail'
        });
      } catch (error) {
        console.error('Failed to send invitation:', error);
        trackEvent('video_invitation_failed', {
          page: 'review_detail'
        });
      } finally {
        setIsSendingInvitation(false);
      }
    }
  };

  useEffect(() => {
    if (isReplying && !generatedReply && !reply) {
      handleGenerateReply();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReplying]);

  // Update replyText when generatedReply actually changes (not just when edit mode toggles)
  useEffect(() => {
    if (generatedReply && generatedReply !== prevGeneratedReply.current && (isReplying || isEditing)) {
      setReplyText(generatedReply);
      prevGeneratedReply.current = generatedReply;
    }
  }, [generatedReply, isReplying, isEditing]);

  // Update the original reply text ref when reply prop changes
  useEffect(() => {
    originalReplyText.current = reply?.text || '';
  }, [reply?.text]);


  const displayedText = reviewText
    ? isExpanded
      ? reviewText
      : `${reviewText?.substring(0, characterLimit)}...`
    : '';

  const Loader = () => <CircularProgress size={24} />;

  // If success screen should be shown, render it instead of the review
  if (showSuccessScreen) {
    return (
      <Box className={classes.successScreen}>
        <CheckCircleIcon className={classes.successIcon} />
        <Typography className={classes.successTitle}>
          Success!
        </Typography>
        <Typography className={classes.successMessage}>
          Invitation sent to {profileName}!<br />
          We'll notify you when<br />
          they upload their video.
        </Typography>
        <Button
          className={classes.backToReviewButton}
          onClick={async () => {
            trackEvent('back_to_review_clicked', {
              page: 'review_detail'
            });
            
            // Refetch the review to update invitationId before going back
            if (originalToken) {
              await dispatch(fetchGbpReviewById({
                reviewId,
                accountId,
                token: originalToken
              }));
            }
            
            setShowSuccessScreen(false);
          }}
        >
          Back to review
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      paddingBottom: !invitationId ? '200px' : '0',
      '@media (max-width: 600px)': {
        paddingBottom: !invitationId ? '160px' : '0',
      },
      '@media (max-height: 700px)': {
        paddingBottom: !invitationId ? '140px' : '0',
      },
    }}>
      <Grid container spacing={1.5} className={classes.reviewContainer}>
        <Grid item>
          <Avatar src={profilePhotoUrl} className={classes.avatar} />
        </Grid>
        <Grid item xs>
          <Box display="flex" alignItems="center">
            <Typography className={classes.profileName}>{profileName}</Typography>
            <Typography variant="body2" className={classes.timestamp}>
              {formatTimestamp2(timestamp)}
            </Typography>
          </Box>
          <div style={{ marginBottom: '10px'}}>
          <Rating value={rating} readOnly size="small" />
          {!reply && !isReplying && !isSubmitting && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ChatOutlinedIcon />}
              className={classes.replyButton}
              onClick={() => {
                trackEvent('reply_started', {
                  page: 'review_detail'
                });
                setIsReplying(true);
              }}
            >
              Reply
            </Button>
        )}
          </div>
         
          <Typography className={classes.reviewText}>
            {displayedText}
            {isTruncated && !isExpanded && (
              <span className={classes.moreLink} onClick={toggleIsExpanded}>
                ... More
              </span>
            )}
            {isTruncated && isExpanded && (
              <span className={classes.moreLink} onClick={toggleIsExpanded}>
                &nbsp;Less
              </span>
            )}
          </Typography>
        </Grid>
        
      </Grid>

      {isSubmitting ? (
        <Box className={classes.loaderContainer}>
          <Loader />
        </Box>
      ) : (
        <>
          {reply && (
            <Box className={classes.replyContainer}>
              <Grid container spacing={1.5}>
                <Grid item>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <VerifiedIcon
                        color="primary"
                        sx={{ fontSize: '1.2rem', color: '#0788F5', backgroundColor: 'white', borderRadius: '50%' }}
                      />
                    }
                  >
                    <Avatar className={classes.businessAvatar} style={{ backgroundColor: '#5242EA' }}>
                      <BusinessIcon style={{ color: 'white', backgroundColor: '#5242EA' }} />
                    </Avatar>
                  </Badge>
                </Grid>
                <Grid item xs>
                  <Box display="flex" alignItems="baseline">
                    <Typography className={classes.profileName}>{reply.businessName}</Typography>
                    <Typography variant="body2" className={classes.ownerLabel}>
                      Owner
                    </Typography>
                  </Box>
                  <Typography variant="body2" className={classes.timestamp}>
                    {formatTimestamp(reply.timestamp)}
                  </Typography>
                  {isEditing ? (
                    <>
                      <Box className={classes.aiAssistHeader} mt={1}>
                        <Box className={classes.aiAssistTitle}>
                          <AutoAwesomeIcon className={classes.aiIcon} />
                          <Typography variant="body2" fontWeight={600}>
                            AI Assist Draft Reply
                          </Typography>
                        </Box>
                        <IconButton 
                          className={classes.closeButton}
                          size="small"
                          onClick={() => {
                            trackEvent('reply_edit_cancelled', {
                              page: 'review_detail'
                            });
                            dispatch(clearGeneratedReply({ reviewId }));
                            prevGeneratedReply.current = undefined;
                            setIsEditing(false);
                            setReplyText(originalReplyText.current);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {isGenerating ? (
                        <Box className={classes.loaderContainer}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" style={{ marginLeft: '8px' }}>
                            Generating reply...
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <TextField
                            fullWidth
                            multiline
                            maxRows={8}
                            variant="outlined"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Button
                              onClick={() => {
                                trackEvent('reply_edit_cancelled', {
                                  page: 'review_detail'
                                });
                                dispatch(clearGeneratedReply({ reviewId }));
                                prevGeneratedReply.current = undefined;
                                setIsEditing(false);
                                setReplyText(originalReplyText.current);
                              }}
                              style={{ color: '#5242EA' }}
                            >
                              Cancel
                            </Button>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="outlined"
                                startIcon={<ReplayIcon />}
                                onClick={() => {
                                  handleGenerateReply();
                                }}
                                style={{ backgroundColor: '#E1E5EE', borderColor: '#C4C4C4', color: '#101828' }}
                              >
                                Regenerate
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleReply}
                                style={{ backgroundColor: '#5242EA' }}
                              >
                                Reply
                              </Button>
                            </Box>
                          </Box>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography className={classes.replyText}>{reply.text}</Typography>
                      <Box mt={1}>
                        <Button
                          startIcon={<EditIcon />}
                          className={classes.editDeleteButton}
                          onClick={() => {
                            trackEvent('reply_edit_started', {
                              page: 'review_detail'
                            });
                            setReplyText(originalReplyText.current);
                            setIsEditing(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          startIcon={isDeletingReply ? <CircularProgress size={16} /> : <DeleteIcon />}
                          className={classes.editDeleteButton}
                          onClick={handleDelete}
                          disabled={isDeletingReply}
                        >
                          {isDeletingReply ? 'Deleting...' : 'Delete'}
                        </Button>
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
          {isReplying && !reply && (
            <Box className={classes.replyContainer}>
              <Grid container spacing={1.5}>
                <Grid item>
                  <AutoAwesomeIcon className={classes.aiIcon} />
                </Grid>
                <Grid item xs>
                  <Box className={classes.aiAssistHeader}>
                    <Box className={classes.aiAssistTitle}>
                      <Typography variant="body2" fontWeight={500}>
                        AI Assist Draft Reply
                      </Typography>
                    </Box>
                    <IconButton 
                      className={classes.closeButton}
                      size="small"
                      onClick={() => {
                        trackEvent('reply_cancelled', {
                          page: 'review_detail'
                        });
                        dispatch(clearGeneratedReply({ reviewId }));
                        prevGeneratedReply.current = undefined;
                        setIsReplying(false);
                        setReplyText('');
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {isGenerating ? (
                    <Box className={classes.loaderContainer}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" style={{ marginLeft: '8px' }}>
                        Generating reply...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={8}
                        variant="outlined"
                        placeholder="Write a reply..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Button
                          onClick={() => {
                            trackEvent('reply_cancelled', {
                              page: 'review_detail'
                            });
                            dispatch(clearGeneratedReply({ reviewId }));
                            prevGeneratedReply.current = undefined;
                            setIsReplying(false);
                            setReplyText('');
                          }}
                          style={{ color: '#5242EA' }}
                        >
                          Cancel
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<ReplayIcon />}
                            onClick={() => {
                              handleGenerateReply();
                            }}
                            style={{ backgroundColor: '#E1E5EE' }}
                          >
                            Regenerate
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleReply}
                            style={{ backgroundColor: '#5242EA' }}
                          >
                            Reply
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Perfect Time to Ask for Video Block - show only if invitationId is not set */}
        <Box className={classes.videoInviteBlock}>
          <Typography className={classes.videoInviteTitle}>
            Perfect Time to Ask for Video
          </Typography>
          <Typography className={classes.videoInviteDescription}>
            Send an SMS invite to upload a<br />
            video of their experience.<br />
            - Cell number required -
          </Typography>
          <Button 
            className={classes.askVideoButton}
            onClick={() => {
              trackEvent('ask_for_video_clicked', {
                page: 'review_detail'
              });
              setShowPhoneModal(true);
            }}
          >
            Ask For A Video
          </Button>
        </Box>
  

      {/* Phone Number Modal */}
      <Dialog 
        open={showPhoneModal} 
        onClose={() => {
          if (!isSendingInvitation) {
            setShowPhoneModal(false);
            setPhoneNumber('');
            setFormattedPhoneNumber('');
            setIsPhoneValid(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: '600px',
            overflow: 'visible',
          },
        }}
      >
        <DialogTitle>
          <Typography className={classes.modalTitle}>
            Send a Video invitation to {profileName}?
          </Typography>
        </DialogTitle>
        <DialogContent  style={ {
            maxHeight: '600px',
            overflow: 'visible',
          }}>
          <Typography className={classes.modalDescription}>
            We need {profileName}'s cell number to send the SMS invitation.
          </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="(555) 555-1234"
              value={formattedPhoneNumber}
              onChange={(e) => {
                const input = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters
                if (input.length <= 10) {
                  setPhoneNumber(input);

                  const asYouType = new AsYouType('US');
                  const formatted = asYouType.input(input);
                  setFormattedPhoneNumber(formatted);

                  // Validate the number
                  if (input.length >= 10) {
                    try {
                      const isValid = isValidPhoneNumber(input, 'US');
                      if (isValid) {
                        const parsedNumber = parsePhoneNumber(input, 'US');
                        const validTypes = ['MOBILE', 'FIXED_LINE', 'FIXED_LINE_OR_MOBILE'];
                        const typeValid = !parsedNumber.getType() || validTypes.includes(parsedNumber.getType() || '');
                        setIsPhoneValid(isValid && typeValid);
                      } else {
                        setIsPhoneValid(false);
                      }
                    } catch (error) {
                      setIsPhoneValid(false);
                    }
                  } else {
                    setIsPhoneValid(false);
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                    +1
                  </Box>
                ),
              }}
            />
          <Button
            className={classes.sendSmsButton}
            onClick={handleSendInvitation}
            disabled={!isPhoneValid || isSendingInvitation}
          >
            {isSendingInvitation ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                Sending...
              </>
            ) : (
              'Send SMS Invitation'
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}; 